import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ComplianceDashboard({ campaignId }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchDashboard();
  }, [campaignId, startDate, endDate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });

      const response = await axios.get(
        `${API_BASE}/dialer/campaigns/${campaignId}/compliance?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching compliance dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading compliance data...</div>;
  }

  if (!dashboard) {
    return <div className="text-center py-4 text-gray-500">No data available</div>;
  }

  const scoreColor = dashboard.compliance_score >= 95 ? 'text-green-600' :
                     dashboard.compliance_score >= 80 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <Shield className="w-6 h-6" />
          Compliance Dashboard
        </h3>
        <div className="flex gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <p className="text-sm text-gray-600">Compliance Score</p>
          <p className={`text-3xl font-bold ${scoreColor}`}>
            {dashboard.compliance_score}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">DNC Violations</p>
          <p className="text-3xl font-bold text-red-600">
            {dashboard.dnc_violations}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Time Violations</p>
          <p className="text-3xl font-bold text-orange-600">
            {dashboard.time_violations}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Total Calls</p>
          <p className="text-3xl font-bold">
            {dashboard.total_calls}
          </p>
        </div>
      </div>

      {dashboard.total_violations > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">
            {dashboard.total_violations} compliance violation(s) detected. Please review and take corrective action.
          </p>
        </div>
      )}

      {dashboard.total_violations === 0 && dashboard.total_calls > 0 && (
        <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-green-800">
            All calls are compliant. Great job!
          </p>
        </div>
      )}
    </div>
  );
}


